import React, { useState, useEffect } from 'react';
import { externalBookingAPI } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Check, X, Eye, Settings, Calendar, Clock, Phone, Mail, User, Image, AlertTriangle, UserCheck, FolderOpen, Trash2, HardDrive } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatTimeRange } from '@/utils/formatters';

const ExternalApproval = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [mockFee, setMockFee] = useState(0);
  const [bkashNumber, setBkashNumber] = useState('01995432873');
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(1);
  const [showSeats, setShowSeats] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Files
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [filesData, setFilesData] = useState({ total_count: 0, total_size_mb: 0, files: [] });
  const [filesLoading, setFilesLoading] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(7);
  const [cleanupProcessing, setCleanupProcessing] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  
  // Change Speaking Slot
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [changingSlot, setChangingSlot] = useState(false);

  useEffect(() => {
    fetchApprovals();
  }, [statusFilter]);

  useEffect(() => {
    if (isAdmin) fetchSettings();
  }, [isAdmin]);

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const res = await externalBookingAPI.getApprovals(statusFilter);
      setApprovals(res.data);
    } catch (error) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await externalBookingAPI.getSettings();
      setMockFee(res.data.mock_fee || 0);
      setBkashNumber(res.data.bkash_number || '01995432873');
      setMaxFileSizeMb(res.data.max_file_size_mb || 1);
      setShowSeats(res.data.show_seats !== false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchFiles = async () => {
    setFilesLoading(true);
    try {
      const res = await externalBookingAPI.listFiles();
      setFilesData(res.data);
    } catch (error) {
      toast.error('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  };

  const handleDeleteFile = async (bookingId) => {
    if (!window.confirm('Delete this payment screenshot? This cannot be undone.')) return;
    try {
      await externalBookingAPI.deleteFile(bookingId);
      toast.success('Screenshot deleted');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleBulkCleanup = async () => {
    if (!window.confirm(`Delete all payment screenshots from bookings older than ${cleanupDays} day(s)?`)) return;
    setCleanupProcessing(true);
    try {
      const res = await externalBookingAPI.cleanupOldFiles(cleanupDays);
      toast.success(res.data.message || 'Cleanup done');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to cleanup');
    } finally {
      setCleanupProcessing(false);
    }
  };

  const openSlotPicker = async () => {
    if (!selectedBooking) return;
    setSlotsLoading(true);
    setShowSlotPicker(true);
    try {
      const res = await externalBookingAPI.getAvailableSlots(selectedBooking.id);
      setAvailableSlots(res.data);
    } catch (error) {
      toast.error('Failed to load available slots');
      setShowSlotPicker(false);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleChangeSlot = async (newSlotId) => {
    if (!selectedBooking || !newSlotId) return;
    setChangingSlot(true);
    try {
      const res = await externalBookingAPI.changeSpeakSlot(selectedBooking.id, newSlotId);
      toast.success('Speaking slot changed successfully');
      // Update local selectedBooking with new slot info
      const ns = res.data.new_slot;
      setSelectedBooking({
        ...selectedBooking,
        speak_slot_id: ns.id,
        speak_date: ns.speak_date,
        speak_start: ns.start_time,
        speak_end: ns.end_time
      });
      setShowSlotPicker(false);
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change slot');
    } finally {
      setChangingSlot(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleApprove = async (bookingId) => {
    setProcessing(true);
    try {
      await externalBookingAPI.approveBooking(bookingId);
      toast.success('Booking approved! Confirmation email sent.');
      setShowDetail(false);
      fetchApprovals();
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.detail || 'Failed to approve';
      if (status === 409) {
        // Seat taken in the meantime — show prominent warning
        toast.error(msg, { duration: 7000, icon: '⚠️' });
        fetchApprovals();
      } else {
        toast.error(msg);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedBooking) return;
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      toast.error('Please provide a rejection reason');
      return;
    }
    if (trimmed.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setProcessing(true);
    try {
      await externalBookingAPI.rejectBooking(selectedBooking.id, trimmed);
      toast.success('Booking rejected. Notification sent.');
      setShowRejectModal(false);
      setShowDetail(false);
      setRejectReason('');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await externalBookingAPI.updateSettings({ mock_fee: mockFee, bkash_number: bkashNumber, max_file_size_mb: maxFileSizeMb, show_seats: showSeats });
      toast.success('Settings updated');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Pending</Badge>,
      approved: <Badge className="bg-green-100 text-green-800 border-green-300">Approved</Badge>,
      rejected: <Badge className="bg-red-100 text-red-800 border-red-300">Rejected</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold">External Booking Approvals</h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <Button data-testid="external-files-btn" variant="outline" size="sm" onClick={() => { setShowFilesModal(true); fetchFiles(); }}>
                <FolderOpen className="h-4 w-4 mr-1" /> Files
              </Button>
              <Button data-testid="external-settings-btn" variant="outline" size="sm" onClick={() => setShowSettings(true)}>
                <Settings className="h-4 w-4 mr-1" /> Settings
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      ) : approvals.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            No {statusFilter} booking requests
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {approvals.map((booking) => (
            <Card key={booking.id} data-testid={`approval-card-${booking.id}`} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{booking.candidate_name}</span>
                      {getStatusBadge(booking.status)}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{booking.candidate_mobile}</span>
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{booking.candidate_email}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />LRW: {formatDate(booking.lrw_date)}</span>
                      <span>{booking.mock_uid}</span>
                    </div>
                    <div className="text-xs text-gray-400">
                      Submitted: {new Date(booking.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {booking.internal_matches && booking.internal_matches.length > 0 && (
                      <div
                        data-testid={`internal-match-alert-${booking.id}`}
                        className="mt-2 inline-flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-2.5 py-1.5 text-xs"
                      >
                        <UserCheck className="h-3.5 w-3.5 text-amber-700 mt-0.5 shrink-0" />
                        <div className="text-amber-900">
                          <span className="font-semibold">Already a Radiance student!</span>{' '}
                          {booking.internal_matches.map((m, i) => (
                            <span key={m.id}>
                              {i > 0 && ', '}
                              <span className="font-medium">{m.name}</span>
                              {m.batch_code && (
                                <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px] font-bold tracking-wide">
                                  {m.batch_code}
                                </span>
                              )}
                              <span className="text-amber-700"> (matched by {m.match_field})</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      data-testid={`view-booking-${booking.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedBooking(booking); setShowDetail(true); }}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                    {booking.status === 'pending' && (
                      <>
                        <Button
                          data-testid={`approve-booking-${booking.id}`}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(booking.id)}
                          disabled={processing}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          data-testid={`reject-booking-${booking.id}`}
                          variant="destructive"
                          size="sm"
                          onClick={() => { setSelectedBooking(booking); setShowRejectModal(true); }}
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Request Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              {selectedBooking.internal_matches && selectedBooking.internal_matches.length > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <UserCheck className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-900 space-y-1.5">
                      <p className="font-semibold">⚠ Existing Radiance Student Detected</p>
                      <p className="text-xs text-amber-800">This external booking's contact info matches an internal student record:</p>
                      {selectedBooking.internal_matches.map((m) => (
                        <div key={m.id} className="text-xs bg-white/70 rounded px-2 py-1.5">
                          <div><span className="font-medium">Name:</span> {m.name}</div>
                          {m.batch_code && <div><span className="font-medium">Batch:</span> <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-bold tracking-wide">{m.batch_code}</span></div>}
                          {m.candidate_no && <div><span className="font-medium">Candidate No:</span> {m.candidate_no}</div>}
                          <div><span className="font-medium">Match field:</span> {m.match_field}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-gray-400">Name</Label>
                  <p className="font-medium">{selectedBooking.candidate_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Mobile</Label>
                  <p className="font-medium">{selectedBooking.candidate_mobile}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Email</Label>
                  <p className="font-medium">{selectedBooking.candidate_email}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Previous Mock</Label>
                  <p className="font-medium">{selectedBooking.previous_mock === 'yes' ? `Yes (${selectedBooking.mock_count || 'N/A'})` : 'No'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Mock UID</Label>
                  <p className="font-medium">{selectedBooking.mock_uid || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Status</Label>
                  <p>{getStatusBadge(selectedBooking.status)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm border-t pt-3">
                <div>
                  <Label className="text-xs text-gray-400">LRW Date</Label>
                  <p className="font-medium">{formatDate(selectedBooking.lrw_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">LRW Time</Label>
                  <p className="font-medium">{formatTimeRange(selectedBooking.lrw_start, selectedBooking.lrw_end)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Speaking Date</Label>
                  <p className="font-medium">{formatDate(selectedBooking.speak_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Speaking Time</Label>
                  <p className="font-medium">{formatTimeRange(selectedBooking.speak_start, selectedBooking.speak_end)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Speaking Mode</Label>
                  <p className="font-medium">{selectedBooking.speak_mode}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Campus</Label>
                  <p className="font-medium">{selectedBooking.campus || 'Main Campus'}</p>
                </div>
              </div>

              {/* Payment Screenshot */}
              {selectedBooking.payment_screenshot && (
                <div className="border-t pt-3">
                  <Label className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Image className="h-3 w-3" />Payment Screenshot <span className="text-blue-600 ml-1">(Click to view full size)</span></Label>
                  <img
                    data-testid="payment-screenshot-thumb"
                    src={selectedBooking.payment_screenshot}
                    alt="Payment"
                    className="max-w-full max-h-64 rounded-lg border cursor-zoom-in hover:opacity-90 transition"
                    onClick={() => setShowImagePreview(true)}
                  />
                  {selectedBooking.payment_amount > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Amount: BDT {selectedBooking.payment_amount}</p>
                  )}
                </div>
              )}

              {selectedBooking.reviewed_by && (
                <div className="border-t pt-3 text-xs text-gray-400">
                  Reviewed by: {selectedBooking.reviewed_by} | {selectedBooking.reviewed_at && new Date(selectedBooking.reviewed_at).toLocaleString()}
                  {selectedBooking.reject_reason && <p className="text-red-500 mt-1">Reason: {selectedBooking.reject_reason}</p>}
                </div>
              )}

              {selectedBooking.status === 'pending' && (
                <>
                  <div className="border-t pt-3">
                    <Button
                      data-testid="change-slot-btn"
                      variant="outline"
                      size="sm"
                      className="w-full border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
                      onClick={openSlotPicker}
                    >
                      <Clock className="h-4 w-4 mr-1" /> Change Speaking Slot
                    </Button>
                    <p className="text-xs text-gray-500 mt-1.5 text-center">Use if the original slot got taken by an internal student, or to move the candidate to a different time.</p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(selectedBooking.id)}
                      disabled={processing}
                    >
                      <Check className="h-4 w-4 mr-1" /> {processing ? 'Processing...' : 'Approve'}
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => setShowRejectModal(true)}
                    >
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-size Payment Screenshot Preview Modal */}
      <Dialog open={showImagePreview} onOpenChange={setShowImagePreview}>
        <DialogContent className="max-w-4xl w-[calc(100%-16px)] p-0 border-0 bg-transparent shadow-none">
          <div className="relative bg-black/90 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowImagePreview(false)}
              data-testid="close-image-preview"
              className="absolute top-2 right-2 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-1.5 backdrop-blur-sm transition"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            {selectedBooking?.payment_screenshot && (
              <img
                src={selectedBooking.payment_screenshot}
                alt="Payment Screenshot Full Size"
                className="w-full max-h-[85vh] object-contain"
              />
            )}
            {selectedBooking?.payment_screenshot && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full">
                {selectedBooking.candidate_name} — Payment Screenshot
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Speaking Slot Modal */}
      <Dialog open={showSlotPicker} onOpenChange={setShowSlotPicker}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" />
              Pick a New Speaking Slot
            </DialogTitle>
          </DialogHeader>
          {slotsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 mb-2">Only slots not yet booked can be selected. The current slot is highlighted.</p>
              {availableSlots.length === 0 ? (
                <p className="text-center py-6 text-gray-500 text-sm">No speaking slots for this mock.</p>
              ) : (
                availableSlots.map((slot) => {
                  const blocked = slot.is_full && !slot.is_current;
                  return (
                    <div
                      key={slot.id}
                      data-testid={`pick-slot-${slot.id}`}
                      onClick={() => !blocked && !slot.is_current && handleChangeSlot(slot.id)}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                        slot.is_current ? 'border-blue-500 bg-blue-50 cursor-default' :
                        blocked ? 'border-red-200 bg-red-50 opacity-70 cursor-not-allowed' :
                        'border-gray-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-sm text-gray-900">{formatDate(slot.speak_date)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{formatTimeRange(slot.start_time, slot.end_time)} {slot.room ? `| ${slot.room}` : ''}</p>
                      </div>
                      <Badge className={
                        slot.is_current ? 'bg-blue-600 text-white' :
                        blocked ? 'bg-red-100 text-red-700 border-red-300' :
                        'bg-green-100 text-green-700 border-green-300'
                      }>
                        {slot.is_current ? 'Current' : blocked ? 'Taken' : 'Available'}
                      </Badge>
                    </div>
                  );
                })
              )}
              {changingSlot && (
                <p className="text-xs text-blue-600 text-center pt-2">Updating slot...</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={(open) => { if (!open) setRejectReason(''); setShowRejectModal(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Reject Booking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
              The candidate will receive an email with the reason you provide below. Please be specific and respectful.
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Rejection Reason <span className="text-red-500">*</span></Label>
              <Textarea
                data-testid="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Payment screenshot could not be verified, slot conflict, incomplete information..."
                rows={4}
                maxLength={1000}
                className="resize-none"
              />
              <p className="text-xs text-gray-400 text-right">{rejectReason.length}/1000</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>Cancel</Button>
            <Button
              data-testid="confirm-reject-btn"
              variant="destructive"
              onClick={handleReject}
              disabled={processing || !rejectReason.trim()}
            >
              {processing ? 'Rejecting...' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>External Booking Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mock Test Fee (BDT)</Label>
              <Input
                data-testid="mock-fee-input"
                type="number"
                value={mockFee}
                onChange={(e) => setMockFee(parseFloat(e.target.value) || 0)}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Bkash Merchant Number</Label>
              <Input
                data-testid="bkash-number-input"
                value={bkashNumber}
                onChange={(e) => setBkashNumber(e.target.value)}
                placeholder="01XXXXXXXXX"
              />
            </div>
            <div>
              <Label>Max Payment Screenshot Size (MB)</Label>
              <Input
                data-testid="max-file-size-input"
                type="number"
                step="0.1"
                min="0.1"
                max="10"
                value={maxFileSizeMb}
                onChange={(e) => setMaxFileSizeMb(parseFloat(e.target.value) || 1)}
                placeholder="1"
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 1-3 MB. Range: 0.1 - 10 MB.</p>
            </div>
            <div className={`flex items-start gap-3 p-3 rounded-lg border ${showSeats ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
              <input
                id="show-seats-toggle"
                data-testid="show-seats-toggle"
                type="checkbox"
                checked={showSeats}
                onChange={(e) => setShowSeats(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <div className="flex-1">
                <Label htmlFor="show-seats-toggle" className="font-medium cursor-pointer">
                  Show seats-left count on external mock cards
                </Label>
                <p className="text-xs text-gray-500 mt-1">If unchecked, the "X Seats Left" text will be hidden from the public <code className="px-1 bg-white rounded">/mock-booking</code> page.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Files Management Modal */}
      <Dialog open={showFilesModal} onOpenChange={setShowFilesModal}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600" />
              Payment Screenshots — Files Management
            </DialogTitle>
          </DialogHeader>
          {filesLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700 font-medium">Total Files</p>
                  <p className="text-2xl font-bold text-blue-900">{filesData.total_count}</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-xs text-indigo-700 font-medium flex items-center gap-1"><HardDrive className="h-3 w-3" />Total Size</p>
                  <p className="text-2xl font-bold text-indigo-900">{filesData.total_size_mb} MB</p>
                </div>
              </div>

              {/* Bulk Cleanup */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-amber-900 flex items-center gap-1">
                  <Trash2 className="h-4 w-4" />Bulk Cleanup
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-amber-800">Delete screenshots older than</span>
                  <Input
                    data-testid="cleanup-days-input"
                    type="number"
                    min="1"
                    max="365"
                    value={cleanupDays}
                    onChange={(e) => setCleanupDays(parseInt(e.target.value, 10) || 7)}
                    className="w-20 h-8 text-sm"
                  />
                  <span className="text-xs text-amber-800">day(s)</span>
                  <Button
                    data-testid="bulk-cleanup-btn"
                    size="sm"
                    variant="destructive"
                    className="ml-auto"
                    onClick={handleBulkCleanup}
                    disabled={cleanupProcessing || filesData.total_count === 0}
                  >
                    {cleanupProcessing ? 'Cleaning...' : 'Run Cleanup'}
                  </Button>
                </div>
                <p className="text-xs text-amber-700">Booking records will be kept — only the payment screenshot images are removed to free up database space.</p>
              </div>

              {/* Files list */}
              {filesData.files.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">No payment screenshots found.</div>
              ) : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                  {filesData.files.map((f) => (
                    <div key={f.booking_id} data-testid={`file-row-${f.booking_id}`} className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-lg p-2.5 hover:border-blue-300 transition">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">{f.candidate_name}</span>
                          {getStatusBadge(f.status)}
                          {f.age_days !== null && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${f.age_days >= 7 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                              {f.age_days}d old
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 text-xs text-gray-500 mt-0.5">
                          <span>{f.mock_uid}</span>
                          <span>{f.candidate_mobile}</span>
                          <span className="font-mono text-blue-700">{formatBytes(f.size_bytes)}</span>
                        </div>
                      </div>
                      <Button
                        data-testid={`delete-file-${f.booking_id}`}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 shrink-0"
                        onClick={() => handleDeleteFile(f.booking_id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExternalApproval;
